import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { API_URL } from 'config';
import { getTokens } from 'utils/tokens';
import { GptEngine, GPTOptionParams, GPTOptions, IGptOptions, IGPTRule, IGPTRuleExtended, RuleForCallResponse } from 'store/gpt/types';
import { IGPTQuestion } from '../widgets/GPTSettings/types';
import { getAnswersFromRuleResponse, getEngineOptions } from './utils/gpt';

interface IUpdateGPTRuleById {
	id: string;
	body: Partial<IGPTRuleExtended>;
}

interface ITagForGpt {
	tagName: string;
	text?: string;
	choices?: string;
}

interface IRunRuleBody {
	gptOptions: IGptOptions;
	assistantText?: string;
	questions?: string[];
	tags?: ITagForGpt[];
}

export interface GptEngineParams {
	currentGptEngine: GptEngine;
	models: string[];
	defaultModel: string;
	additionalParams: Record<string, GPTOptionParams>
}

export const gptAPI = createApi({
	reducerPath: 'gptApi',
	baseQuery: fetchBaseQuery({
		baseUrl: API_URL,
		prepareHeaders: (headers) => {
			const { token } = getTokens();
			if (token) {
				headers.set('authorization', `Bearer ${token}`);
			}
			return headers;
		},
	}),
	tagTypes: ['gptOptions'],
	endpoints: (builder) => ({
		// queries
		getGPTRules: builder.query<IGPTRule[], void>({
			query: () => ({
				url: '/gpt/',
			}),
		}),
		getGPTRuleById: builder.query<IGPTRuleExtended, string>({
			query: (id) => ({
				url: `/gpt/${id}`,
			}),
		}),
		getGPTEngines: builder.query<GptEngine[], void>({
			query: () => ({
				url: '/gpt/gpt_engine_selectors',
			}),
		}),
		getGPTOptions: builder.query<GptEngineParams, GptEngine>({
			query: (engine) => ({
				url: `/gpt${engine}/gpt_options`,
			}),
			transformResponse: (response: GPTOptions, meta, arg) => getEngineOptions(response, arg),
		}),
		// mutations
		// создание нового правила
		createGPTRule: builder.mutation<string, IGPTRuleExtended>({
			query: (body) => ({
				url: '/gpt/',
				method: 'POST',
				body,
			}),
		}),
		// сохранение изменений существующего правила
		updateGPTRuleById: builder.mutation<void, IUpdateGPTRuleById>({
			query: ({ id, body }) => ({
				url: `/gpt/${id}`,
				method: 'PATCH',
				body,
			}),
		}),
		// удаление правила по id
		deleteGPTRuleById: builder.mutation<void, string>({
			query: (id) => ({
				url: `/gpt/${id}`,
				method: 'DELETE',
			}),
		}),
		// получение ответа на применения правила/произвольного запроса для звонка
		runGPTRuleForCall: builder.mutation<string[], {callId: string; body: IRunRuleBody; type: 'rule' | 'query', questions?:  IGPTQuestion[]}>({
			query: ({ callId, body }) => ({
				url: `/gpt/gpt_request/${callId}/request`,
				method: 'POST',
				body,
			}),
			transformResponse: (response: RuleForCallResponse, meta, arg) => {
				if (arg.type === 'query') return response.responseData.answers;

				return getAnswersFromRuleResponse(
					response.responseData.answers,
					response.responseData.tags,
					arg?.questions,
				);
			},
			transformErrorResponse: (response: { status: string | number }) => response.status,
		}),
	}),
});
